import { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import { TributeItem } from "c8-tributejs";
import GetRequest from "csi/comm/GetRequest";
import html from "csi/html";
import csi from "csi/main";
import { MS_TEAMS } from "csi/service/rightPaneService";
import session from "csi/session";
import sessionConstants from "csi/sessionConstants";
import { rightPaneStore, routerStore, sessionStore } from "csi/state/rootStore";
import util from "csi/util";
import { PURIFY_OPTIONS, purifyForHtml } from "csi/utility/htmlUtility";
import { LOCATION_REVISION_URL, LOCATION_RIGHT_PANE } from "csi/utility/routerUtility";
import xml from "csi/xml";
import { getMentionedUsers, MentionUser } from "designSystem/components/editor/tributeService";
import { ConfirmDialog } from "designSystem/index";
import { ComponentItem } from "designSystem/types/component-item-types";
import _, { Dictionary } from "lodash";
import { action, computed, makeObservable, observable, override, runInAction } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import { ChannelItem, MESSAGE_TIME_FORMAT } from "src/Chat/api";
import { ChatStore, updateChannelOnNode, UpdateChannelOnNodeOption } from "src/Chat/component/ChatStore";
import {
	Channel,
	ChannelMembershipType,
	ChatMessage,
	ChatMessageCollectionPage,
	ChatMessageType,
	createChannel,
	getChannelDetails, getListOfMessagesOfChannel, getListOfPrivateChannels,
	getListOfPublicChannels, getListOfTeams,
	getUserProfilePhoto,
	getUsers,
	MessageGroup,
	MSTeamsApiError,
	mutateRichTextForPost,
	Operations,
	postMessage,
	Reaction,
	ReactionMap,
	subscribeChannel,
	Team, User
} from "../api";
import { MSTeamsUserProvider } from "./MSTeamsUserProvider";

/** panels for ms teams */
export enum MSTeamsPanel {
	JOIN_MSTEAMS,
	CONNECT_TO_MSTEAMS,
	CHANNEL_SELECTION,
	ADD_CHANNEL,
	DISPLAY_CHANNEL,
}

type UserImageMap = Record<string, string>;

export const MSTEAMS_MODULE = "MSTeams";
export const ATTR_ID_MSTEAMS_CHANNELS = "MSTeamsChannels";
export const CLIENT_URL = "ClientURL";

const MS_TEAMS_REACTION_EMOJI_MAP = {
	angry: "\u{1f621}",
	heart: String.fromCodePoint(10084, 65039),
	laugh: "\u{1f600}",
	like: "\u{1f44d}",
	sad: "\u{1f622}",
	surprised: "\u{1f633}",
};

const EMOJI_IMG_TYPE = "http://schema.skype.com/Emoji";

const NAME_NOT_BEGIN_WITH_REGOP1 = /^(?!\_|\..*$).*/;
const NAME_NOT_END_WITH_REGOP2 = /.*[^.]$/;
const NAME_SPECIAL_SIGNS_REGOP3 = /[~#%&\*\{\}\+\/\\:<>\?\|'",]/g;

// special words to validate
const COM_DICTIONARY = _.fill(Array(9), "COM")
	.map((x, i) => `${x}${i + 1}`);
const LTP_DICTIONARY = _.fill(Array(9), "LPT")
	.map((x, i) => `${x}${i + 1}`);

const NAME_INVALID_DICTIONARY = ["forms", "CON", "CONIN$", "CONOUT$", "PRN", "AUX", "NUL", "desktop.ini", "_vti_", ...COM_DICTIONARY, ...LTP_DICTIONARY];
const TEAM_CHANNEL_SEPARATOR = "#";

const CHANNEL_NAME_GENERAL = "General";

const CFGs = {
	// allow image
	FORBID_TAGS: [
		...PURIFY_OPTIONS.FORBID_TAGS.filter((x) => x != "img"),
	],

	ADD_TAGS: [
		"emoji",
	],
};

export let userImageMap: UserImageMap = {};

const fetchImageWithAuth = async (graphSrc: string, token: string): Promise<Response> => {
	const response = await fetch(graphSrc, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});
	if (!response.ok) {
		throw new Error("Bad Url or Token");
	}
	return response;
};

/** fetch and replace images that need Bearer token */
const fetchAndReplaceMSTeamsImageWithAuth = async (graphSrc: string, uniqueId: string, secondCall?: boolean): Promise<void> => {
	let response;
	try {
		response = await fetchImageWithAuth(graphSrc, sessionStore.userDetails.MSTeamsUserToken);
	} catch (err) {
		if (secondCall) {
			console.warn(`Failed to fetch img: ${graphSrc}`, err);
			return;
		}
		await sessionStore.refreshUserDetails();
		return fetchAndReplaceMSTeamsImageWithAuth(graphSrc, uniqueId, true);
	}

	try {
		const blob = await response.blob();
		const replaceImage = document.getElementById(uniqueId) as HTMLImageElement;
		if (replaceImage) {
			replaceImage.src = URL.createObjectURL(blob);
			userImageMap[graphSrc] = replaceImage.src;
		}
	} catch (err) {
		console.warn(`Failed to get blob from response: ${graphSrc}`, err);
	}
};

/** filter rich text for messaging */
export const filterRichText = (s: string): string => {
	const div = document.createElement("div");
	const stripedString = stripEmoji(s);
	div.innerHTML = stripedString;

	// handle ms teams images
	div.querySelectorAll("img")
		.forEach((img) => {
			if (img.getAttribute("itemtype") === EMOJI_IMG_TYPE) {
				img.replaceWith(document.createTextNode(img.alt));
			} else {
				const graphSrc = img.src;
				if (userImageMap[graphSrc]) {
					img.src = userImageMap[graphSrc];
				} else if (graphSrc.includes("$value")) {
					img.src = csi.image("logo_loader.gif");
					const uniqueId = util.uuid();
					img.id = uniqueId;
					fetchAndReplaceMSTeamsImageWithAuth(graphSrc, uniqueId);
				}
				// remove dimensions from image to keep original aspect ratio - it will be reduced by our own containers
				const { style } = img;
				style.height = style.width = "";
				img.removeAttribute("height");
				img.removeAttribute("width");
			}
		});

	return div.innerHTML;
};

/** strip the emoji tags from MS Teams and replace them with spans */
export function stripEmoji(html: string): string | undefined {
	if (!!html) {
		const div = document.createElement("div");
		div.innerHTML = purifyForHtml(html, CFGs);

		div.querySelectorAll("emoji").forEach((element) => {
			const spanElement = document.createElement("span");
			spanElement.innerHTML = element.getAttribute("alt");

			element.replaceWith(spanElement);
		});

		return div.innerHTML;
	}
}

/** get creation date from message */
export const getCreationDateFromMessage = (chatMessage: ChatMessage): Date => {
	return new Date(chatMessage.createdDateTime * 1000);
};

/** get different reactions from message */
export const getReactionsFromMessage = (reactions: Reaction[]): ReactionMap => {
	const map: ReactionMap = {};
	reactions.forEach((reaction) => {
		const emoji = MS_TEAMS_REACTION_EMOJI_MAP[reaction.reactionType];
		if (map[emoji]) {
			map[emoji]++;
		} else {
			map[emoji] = 1;
		}
	});
	return map;
};

/** get the latest reply from a ChatMessage, if it exists */
export const getLatestReplyFromMessage = (message: ChatMessage): ChatMessage | null => {
	return message.replies?.currentPage.length ? message.replies.currentPage[0] : null;
};

/** get locale time string from message */
export const getLocaleTimeStringFromMessage = (chatMessage: ChatMessage): string => {
	return getCreationDateFromMessage(chatMessage)
		.toLocaleTimeString(undefined, MESSAGE_TIME_FORMAT);
};

export class MSTeamsStore extends ChatStore<Team, User, Channel, MSTeamsApiError, MSTeamsUserProvider, MSTeamsPanel> {
	@observable.ref private _connectedChannels: Channel[] = null;
	@observable.ref private _messages: ChatMessage[] = null;
	@observable.ref private _privateChannels: Channel[] = null;
	@observable.ref private _publicChannels: Channel[] = null;
	@observable.ref private _selectedChannel: ChannelItem<ChannelMembershipType>;
	@observable.ref private _selectedMessage: ChatMessage;
	@observable private _startLoadingPrivateChannel: boolean = false;

	// TODO in another story
	// @observable.ref private _hostedContent: any = null;

	@observable private searchText: string;
	@observable private _withHighPriority = false;

	@observable moreMessages: boolean = true;

	private _skipToken: string;

	//#region  properties to override

	/** the current active page in the panel */
	@override get currentPanel(): MSTeamsPanel {
		const { msTeamsUserId } = sessionConstants;

		if (!msTeamsUserId) {
			return MSTeamsPanel.JOIN_MSTEAMS;
		}

		return this._currentPanel;
	}

	/** indicate whether has any error(validation error, api error...) */
	@override get hasError(): boolean {
		const { error, channelNameErrorResource, newTeamId, newChannelDisplayName, teamIdErrorResource } = this;
		const satisfyRequired = !!newTeamId && !!newChannelDisplayName;

		return !!error || !!channelNameErrorResource || !!teamIdErrorResource || !satisfyRequired;
	}

	/** the resource for the loading placeholder */
	@override get loadingSubtitleRes(): string {
		return "LoadingMSTeamsSubtitle";
	}

	/** only show the add people field if the channel is private */
	@override get onlyShowAddPeopleWhenPrivate(): boolean {
		return true;
	}

	/** ready to create channel */
	@override get readyToCreateChannel(): boolean {
		const { teams } = this;
		return !!teams;
	}

	/** ready to read channel history */
	@override get readyToReadChannelHistory(): boolean {
		const { messages } = this;
		return !!messages;
	}

	/** the teams which this current user is part of  */
	@override get teams(): ComponentItem[] | undefined {
		return this._teams?.map(({ id, displayName }) => ({ id, name: displayName }));
	}

	/** users dictionary to specify for a new channel */
	@override get userDictionary(): ComponentItem[] | undefined {
		const { usersExcludeMyself } = this;

		// exclude the current user
		return usersExcludeMyself?.map(({ userId, displayName }) => ({ id: userId, name: displayName }));
	}

	/** user provider for mention users */
	@override get userProvider(): MSTeamsUserProvider {
		const { usersExcludeMyself } = this;
		return new MSTeamsUserProvider(usersExcludeMyself, userImageMap);
	}

	//#endregion

	//#region custom properties

	/** the current state(node, tab, pane) */
	@computed get clientURL(): string {
		const { locationUrl, locationRevisionUrl } = routerStore;
		const clientURL = session.getLocationForUrl(locationUrl, {
			[LOCATION_REVISION_URL]: locationRevisionUrl,
			[LOCATION_RIGHT_PANE]: MS_TEAMS,
		}, true);

		return clientURL;
	}

	/** generate id for connected channel to stored in C8 */
	@computed get connectedChannelId(): string {
		const { selectedChannel, currentTeamId } = this;

		return `${currentTeamId}${TEAM_CHANNEL_SEPARATOR}${selectedChannel.id}`;
	}

	/** connected channels to a specific C8 instance */
	@computed get connectedChannels(): Channel[] | null {
		const { compareChannels, getTeamName } = this;

		// the connected channels might come from other teams which the current user is not part of
		const channels = this._connectedChannels?.filter(({ webUrl }) => !!getTeamName(webUrl));

		if (!channels) {
			return null;
		}

		return channels.sort(compareChannels);
	}

	/** the selected team id */
	@computed get currentTeamId(): string {
		const { selectedChannel } = this;
		const webUrl = new URL(selectedChannel.webUrl);
		const teamId = webUrl.searchParams.get("groupId");

		return teamId;
	}

	/** function to get the template to render the mention user */
	get mentionUserTemplateFun(): (user: TributeItem<MentionUser>) => string {
		return (user: TributeItem<MentionUser>) => `<span contenteditable="false" class="fr-deletable fr-tribute" mentionuserurl="${user.original.url}"><at id=\"${user.original.id}\">${user.original.fullName}</at></span>`;
	}

	/** channel messages */
	get messages(): ChatMessage[] {
		return this._messages;
	}

	/** channel messages */
	// TODO in another story
	// get hostedContent(): any[] {
	//  return this._hostedContent;
	// }

	/** ask MS Teams permission */
	@computed get msTeamsPermissionsUrl(): string {
		const { clientURL } = this;

		const askMSTeamsPermissionsUrl = new GetRequest(MSTEAMS_MODULE, Operations.ASK_PERMISSION)
			.addParameter(CLIENT_URL, clientURL)
			.toRequest();

		return askMSTeamsPermissionsUrl;
	}

	/** search private channels */
	@computed get searchPrivateChannels(): Channel[] | null {
		const { searchText, privateChannels } = this;

		if (!privateChannels) {
			return null;
		}

		if (searchText) {
			const lowercaseSearchText = searchText.toLocaleLowerCase();
			return privateChannels.filter((channel) => {
				const teamName = this.getTeamName(channel.webUrl);
				return teamName.toLocaleLowerCase()
					.includes(lowercaseSearchText) ||
					channel.displayName.toLocaleLowerCase()
						.includes(lowercaseSearchText);
			});
		}

		return privateChannels;
	}

	/** search public channels */
	@computed get searchPublicChannels(): Channel[] | null {
		const { searchText, publicChannels } = this;

		if (searchText) {
			const lowercaseSearchText = searchText.toLocaleLowerCase();
			return publicChannels.filter((channel) => {
				const teamName = this.getTeamName(channel.webUrl);
				return teamName.toLocaleLowerCase()
					.includes(lowercaseSearchText) ||
					channel.displayName.toLocaleLowerCase()
						.includes(lowercaseSearchText);
			});
		}

		return publicChannels;
	}

	/** web url of selected channel */
	get selectedChannelWebUrl(): string {
		return this.selectedChannel.webUrl;
	}

	/** get team ids */
	@computed get teamDictionary(): Dictionary<Team> {
		return _.keyBy<Team>(this._teams, (team) => team.id);
	}

	/** Split messages into groups by date. */
	@computed get messageGroups(): MessageGroup[] {
		const { messages } = this;

		if (!messages?.length) {
			return [];
		}

		let currentGroup: MessageGroup = null;
		const groups: MessageGroup[] = [];
		messages.forEach((message) => {
			const date = getCreationDateFromMessage(getLatestReplyFromMessage(message) || message);
			const dateAsString = date.toLocaleDateString();

			if (currentGroup == null || currentGroup.dateAsString !== dateAsString) {
				const dateFormatOptions: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric" };
				const groupTitle = date.toLocaleDateString(undefined, dateFormatOptions);
				currentGroup = {
					date,
					dateAsString,
					messages: [],
					groupTitle,
				};
				groups.unshift(currentGroup);
			}

			currentGroup.messages.push(message);
		});

		return groups;
	}

	/** private channels not connected to node */
	@computed get disconnectedPrivateChannels(): Channel[] | undefined {
		return this.searchPrivateChannels?.filter((channel) => !this.msTeamsChannelIds.has(channel.id));
	}

	/** public channels not connected to node */
	@computed get disconnectedPublicChannels(): Channel[] | undefined {
		return this.searchPublicChannels?.filter((channel) => !this.msTeamsChannelIds.has(channel.id));
	}

	/** stringset of msteams channel ids */
	@computed get msTeamsChannelIds(): Set<string> {
		return new Set(this.msTeamsConnectedTeamAndChannels.map((e) => e.split(TEAM_CHANNEL_SEPARATOR)[1]));
	}

	/** entries of connected channel, consists of team id and channel id */
	get msTeamsConnectedTeamAndChannels(): string[] {
		return routerStore.locationNode[ATTR_ID_MSTEAMS_CHANNELS];
	}

	/** user email, identifier of user on MS Teams side */
	get msTeamsUserEmail(): string {
		return this.currentUserDetail.MSTeamsUserId;
	}

	/** private channels which the current user is part of */
	@computed get privateChannels(): Channel[] | null {
		const { compareChannels } = this;

		if (!this._privateChannels) {
			return null;
		}

		return this._privateChannels.sort(compareChannels);
	}

	/** public channels which the current user is part of */
	@computed get publicChannels(): Channel[] | null {
		const { compareChannels } = this;

		if (!this._publicChannels) {
			return null;
		}

		return this._publicChannels.sort(compareChannels);
	}

	/** ready to display public channels with team names */
	get readyToDisplayPublicChannelsWithTeamNames(): boolean {
		return !!this.publicChannels && !!this.teams;
	}

	/** ready to display public channels with team names */
	get readyToDisplayPrivateChannelsWithTeamNames(): boolean {
		return !!this.privateChannels && !!this.teams;
	}

	/** ready to display connected channels with team names */
	get readyToDisplayConnectedChannelsWithTeamNames(): boolean {
		return !!this.connectedChannels && !!this.teams;
	}

	/** indicate if show the button to fetch the private channels */
	get showFetchPrivateChannelButton(): boolean {
		const { privateChannels, startLoadingPrivateChannel } = this;
		return !privateChannels && !startLoadingPrivateChannel;
	}

	/** selected Channel */
	get selectedChannel(): ChannelItem<ChannelMembershipType> {
		return this._selectedChannel;
	}

	/** selected ChatMessage */
	get selectedMessage(): ChatMessage {
		return this._selectedMessage;
	}

	/** indicate if start loading the private channels */
	get startLoadingPrivateChannel(): boolean {
		return this._startLoadingPrivateChannel;
	}

	/** users exclude myself */
	get usersExcludeMyself(): User[] | undefined {
		const { users, msTeamsUserEmail } = this;

		// exclude the current user
		return users?.filter((x) => x.email !== msTeamsUserEmail);
	}

	/** check if the user only has view permissions */
	get userIsReadOnly(): boolean {
		return !xml.nodeHasAccess(routerStore.locationNode, xml.RIGHT_WRITE);
	}

	/** indicate if this message has high priority */
	get withHighPriority(): boolean {
		return this._withHighPriority;
	}

	//#endregion

	//#region functions to override

	@override async _closeRightPane(): Promise<void> {
		rightPaneStore.closeSelected();
		this.resetTeamsAndChannels();
	}

	/** fetch the teams */
	@override async _fetchTeams(): Promise<void> {
		if (!!this.teams) {
			return;
		}

		try {
			const results = await getListOfTeams();

			runInAction(() => {
				this._teams = results;
			});
		} catch (error) {
			this.setError(error);

			throw error;
		}
	}

	/** fetch users which are part of the same team */
	@override async _fetchUsers(): Promise<void> {
		const { fetchUserImages, newTeamId, selectedChannel } = this;

		try {
			const results = await getUsers(newTeamId, selectedChannel?.id);

			// fetch user images
			for (const { userId } of results) {
				await fetchUserImages(userId);
			}

			runInAction(() => {
				this._users = results;
			});
		} catch (error) {
			this.setError(error);
		}
	}

	/** create channel */
	@override async _handleOnSaveNewChannel(): Promise<boolean> {
		const {
			channelIsPrivate,
			connectAndNavigateToChannelDisplay,
			newChannelDescription,
			newChannelDisplayName,
			newTeamId,
			resetMessages,
			selectedUsers,
			validateChannelName,
			validateTeam,
		} = this;

		if (!validateChannelName() || !validateTeam()) {
			return false;
		}

		const channelDescription = newChannelDescription.trim();
		const channelName = newChannelDisplayName.trim();

		const userIDs: string[] = selectedUsers?.map((user) => user.id);

		try {
			html.showWait(true);
			const channel = await createChannel(newTeamId, channelName, channelDescription, channelIsPrivate, userIDs);

			if (channel?.id) {
				runInAction(() => {
					this._selectedChannel = new ChannelItem<ChannelMembershipType>(
						channel.id,
						channel.displayName,
						channel.membershipType,
						channel.webUrl,
					);

					// reset user so that we can get the appropriate users, take private channels into account
					this._users = null;
					resetMessages();
					connectAndNavigateToChannelDisplay();
				});

				return true;
			} else if (channel.channelNameViolation) {
				runInAction(() => {
					this._channelNameErrorResource = "ChannelNameExists";
				});

				return true;
			}
		} catch (error) {
			this.setError(error);
		} finally {
			html.showWait(false);
		}
	}

	@override async _postMessage(content: string, urlToUnfurl: string): Promise<boolean> {
		const { currentTeamId, fetchMessages, resetImportantStatus, selectedChannel, selectedMessage, withHighPriority } = this;

		try {
			const mentionUsers = getMentionedUsers(content);
			const finalContent = mutateRichTextForPost(content);
			await postMessage(currentTeamId, selectedChannel.id, selectedMessage?.id, finalContent, withHighPriority, urlToUnfurl, mentionUsers);
		} catch (error) {
			this.setError(error);

			return false;
		} finally {
			await fetchMessages("1", true);
			resetImportantStatus();
		}

		return true;
	}

	/** set the name of description */
	@override _setNewChannelDescription(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		const newDescription = event.target.value;
		this._newChannelDescription = newDescription;
	}

	/** set the name of channel */
	@override _setNewChannelName(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		this._newChannelDisplayName = event.target.value;
	}

	@override _setTeam(event: React.ChangeEvent<{}>, teamItem: ComponentItem, reason: AutocompleteChangeReason): void {
		const { fetchUsers, validateTeam } = this;
		this._newTeamId = teamItem.id;
		validateTeam();

		this._selectedUsers = null;
		fetchUsers();
	}

	/** initialization */
	@override initialize(): void {
		this._currentPanel = MSTeamsPanel.CONNECT_TO_MSTEAMS;
	}

	/** validate the channel name */
	@override _validateChannelName(): boolean {
		const { newChannelDisplayName } = this;
		const channelName = newChannelDisplayName.trim();
		let errorResource = "";

		if (channelName.length > 50) {
			errorResource = "ChannelNameIsLong";
		}

		if (!channelName.match(NAME_NOT_BEGIN_WITH_REGOP1) || !channelName.match(NAME_NOT_END_WITH_REGOP2)) {
			errorResource = "MSTeamsChannelNameNotContainsSpecialSign";
		} else if (channelName.match(NAME_SPECIAL_SIGNS_REGOP3)) {
			errorResource = "MSTeamsChannelNameNotSupportCharacters";
		} else if (NAME_INVALID_DICTIONARY.includes(channelName)) {
			errorResource = "MSTeamsChannelNameNotSupportWords";
		}

		this._channelNameErrorResource = errorResource;

		return !errorResource;
	}

	//#endregion

	//#region custom functions 

	/** go back to connected channel page */
	@action backToConnectedChannels = (): void => {
		this.searchText = "";
		this.handleOnBack();
	};

	/** fetch the private channels */
	@action fetchPrivateChannels = async (): Promise<void> => {
		const { privateChannels, teams } = this;

		if (!!privateChannels) {
			return;
		}

		try {
			const teamIds = teams.map(({ id }) => id);
			this._startLoadingPrivateChannel = true;
			const results = await getListOfPrivateChannels(teamIds);
			runInAction(() => {
				this._privateChannels = results;
			});
		} catch (error) {
			this.setError(error);

			throw error;
		} finally {
			runInAction(() => {
				this._startLoadingPrivateChannel = false;
			});
		}
	};

	/** fetch the public channels */
	fetchPublicChannels = async (): Promise<void> => {
		const { publicChannels, teams } = this;

		if (!!publicChannels) {
			return;
		}

		try {
			const teamIds = teams.map(({ id }) => id);
			const results = await getListOfPublicChannels(teamIds);

			runInAction(() => {
				this._publicChannels = results;
			});
		} catch (error) {
			this.setError(error);

			throw error;
		}
	};

	/** fetch information for connected channels */
	fetchConnectedChannels = async (): Promise<void> => {
		const { msTeamsConnectedTeamAndChannels } = this;
		const { locationUrl } = routerStore;
		const teams: string[] = [];
		const channels: string[] = [];

		msTeamsConnectedTeamAndChannels.forEach((e) => {
			teams.push(e.split(TEAM_CHANNEL_SEPARATOR)[0]);
			channels.push(e.split(TEAM_CHANNEL_SEPARATOR)[1]);
		});

		try {
			const results = await getChannelDetails(locationUrl, teams, channels);

			runInAction(() => {
				this._connectedChannels = results;
			});
		} catch (error) {
			this.setError(error);

			throw error;
		}
	};

	// fetch user images
	fetchUserImages = async (id: string): Promise<void> => {
		if (userImageMap[id] === undefined) {
			const base64 = await getUserProfilePhoto(id);
			userImageMap[id] = base64 ? `data:image/jpeg;base64,${base64}` : null;
		}
	};

	/** fetch messages */
	fetchMessages = async (pageSize?: string, fetchLatest: boolean = false): Promise<void> => {
		const { messages, selectedChannel, currentTeamId, fetchUserImages, moreMessages, selectedMessage } = this;

		if (!moreMessages && !fetchLatest) {
			return;
		}

		// if this is the first time we're displaying messages from a new channel, clear image cache
		if (!this._skipToken) {
			userImageMap = {};
		}

		try {
			const results: ChatMessageCollectionPage = await getListOfMessagesOfChannel(
				currentTeamId,
				selectedChannel.id,
				selectedMessage?.id,
				pageSize,
				fetchLatest ? "" : this._skipToken,
			);

			if (results.nextPage) {
				const requestUrl = new URL(results.nextPage.requestUrl);
				this._skipToken = requestUrl.searchParams.get("$skiptoken");
			} else {
				runInAction(() => {
					this.moreMessages = false;
				});
			}

			// filter out all other message types
			let newMessages = results.currentPage.filter((m) => m.messageType === ChatMessageType.MESSAGE);

			if (messages) {
				newMessages = fetchLatest ? newMessages.concat(messages) : messages.concat(newMessages);
			}

			if (selectedMessage) {
				newMessages.push(selectedMessage);
			}

			// remove the potential duplicate ones due to the case where new posted message from MS Teams server is the same as the current user post
			newMessages = _.uniqBy(newMessages, ({ id }) => id);

			const uniqueUsersInMessages = _.compact([...new Set(newMessages.map(({ from }) => from?.user?.id || from?.application?.id))]);

			for (const userId of uniqueUsersInMessages) {
				await fetchUserImages(userId);
			}

			runInAction(() => {
				this._messages = newMessages;
			});
		} catch (error) {
			this.setError(error);
		}
	};

	/** fetch hosted content */
	// TODO in another story
	// fetchHostedContent = async (): Promise<void> => {
	//  const { selectedChannel, currentTeamId, messages } = this;

	//  const messagesWithHostedContent = messages.filter(message => message.attachments.length);

	//  try {
	//      const results = [];
	//      for (const message of messagesWithHostedContent) {
	//          results.push(await getListOfHostedContent(currentTeamId, selectedChannel.id, message.id));
	//      }
	//      runInAction(() => {
	//          this._hostedContent = results;
	//      });
	//  } catch (error) {
	//      runInAction(() => {
	//          this._error = error;
	//      });
	//  }
	// }

	/** connect to channel and navigate to this channel */
	@action connectAndNavigateToChannelDisplay = (): void => {
		const { connectedChannelId, navigationToChannelDisplay } = this;

		// reset search so that it'll load all the candidate channels when go back
		this.searchText = "";

		// add channel to connected channels node 
		updateChannelOnNode(
			routerStore.locationNode.$URL,
			connectedChannelId,
			UpdateChannelOnNodeOption.ADD,
			ATTR_ID_MSTEAMS_CHANNELS,
		);

		// fetch necessary data and navigation
		navigationToChannelDisplay();
	};

	/** navigate to channel add panel */
	@action navigationToAddChannel = (): void => {
		const { navigateToPanel, resetAddChannelForm } = this;
		resetAddChannelForm();
		navigateToPanel(MSTeamsPanel.ADD_CHANNEL);
	};

	/** navigate to channel selection panel */
	@action navigationToChannelSelection = (): void => {
		const { fetchPublicChannelsAndTeams, navigateToPanel } = this;
		fetchPublicChannelsAndTeams();
		this._selectedChannel = null;
		navigateToPanel(MSTeamsPanel.CHANNEL_SELECTION);
	};

	/** navigate to channel display page */
	@action navigationToChannelDisplay = (): void => {
		const { fetchUsers, navigateToPanel, resetMessages, subscribeChannel } = this;
		resetMessages();
		subscribeChannel();
		fetchUsers();
		navigateToPanel(MSTeamsPanel.DISPLAY_CHANNEL);
	};

	/** navigate to channel connection page */
	@action navigationToConnectToChannel = (): void => {
		const { navigateToPanel } = this;

		// reset the users to avoid inconsistency
		this._users = null;
		navigateToPanel(MSTeamsPanel.CONNECT_TO_MSTEAMS);
	};

	/** action for when user changes the search text */
	@action onChangeText = (e: ChangeEvent<HTMLInputElement>): void => {
		this.searchText = e.target.value;
	};

	/** handler for channel selection */
	@action onSelectChannel = (channel: ChannelItem<ChannelMembershipType>): void => {
		if (this.selectedChannel) {
			this.selectedChannel.selected = false;
		}

		channel.selected = true;
		this._selectedChannel = channel;
		this._newTeamId = this.getTeamId(channel.webUrl);
	};

	/** mark importance on message */
	@action onSetImportant = (important: boolean): void => {
		this._withHighPriority = important;
	};

	/** reset add channel form */
	@action resetAddChannelForm = (): void => {
		this._channelNameErrorResource = null;
		this._teamIdErrorResource = null;
		this._selectedChannel = null;
		this._channelIsPrivate = false;
		this._newChannelDisplayName = "";
		this._selectedUsers = null;
	};

	/** reset connected channels */
	@action resetConnectedChannels = (): void => {
		this._connectedChannels = null;
	};

	/** reset teams & channels */
	@action resetTeamsAndChannels = (): void => {
		this._channels = null;
		this._teams = null;
	};

	/** reset important status */
	@action resetImportantStatus = (): void => {
		this._withHighPriority = false;
	};

	/** reset messages */
	@action resetMessages = (): void => {
		if (this._messages) {
			this._messages = null;
		}
		this._selectedMessage = null;
		this._skipToken = undefined;
		this.moreMessages = true;
	};

	/** navigate to channel display page */
	@action selectAndNavigateToChannelDisplay = async (channel: ChannelItem<ChannelMembershipType>): Promise<void> => {
		const { navigationToChannelDisplay, onSelectChannel } = this;
		onSelectChannel(channel);
		navigationToChannelDisplay();
	};

	/** navigate out of message replies page */
	@action deselectMessageReplies = (): void => {
		const { fetchMessages, resetMessages } = this;
		resetMessages();
		fetchMessages();
	};

	/** navigate to message replies page */
	@action selectMessageReplies = (message: ChatMessage): void => {
		const { fetchMessages, resetMessages } = this;
		resetMessages();
		this._selectedMessage = _.omit(message, ["replies"]);
		fetchMessages();
	};

	/** subscribe channel for messages notification */
	@action subscribeChannel = async (): Promise<void> => {
		const { selectedChannel, currentTeamId } = this;

		try {
			await subscribeChannel(currentTeamId, selectedChannel.id);
		} catch (error) {
			this.setError(error);
		}
	};

	/** comparison function for sorting channel order */
	compareChannels = (a: Channel, b: Channel): number => {
		const { getTeamName } = this;
		const teamNameA = getTeamName(a.webUrl);
		const teamNameB = getTeamName(b.webUrl);
		if (teamNameA < teamNameB) {
			return -1;
		}
		if (teamNameA > teamNameB) {
			return 1;
		}
		if (a.displayName === CHANNEL_NAME_GENERAL) {
			return -1;
		}
		if (b.displayName === CHANNEL_NAME_GENERAL) {
			return 1;
		}
		return a.displayName.toLocaleLowerCase() < b.displayName.toLocaleLowerCase() ? -1 : 1;
	};

	/** fetch channels and teams */
	fetchPublicChannelsAndTeams = async (): Promise<void> => {
		const { fetchPublicChannels, fetchTeams } = this;
		await fetchTeams();
		fetchPublicChannels();
	};

	/** fetch connected channels and teams */
	fetchConnectChannelsAndTeams = async (): Promise<void> => {
		const { fetchConnectedChannels, fetchTeams } = this;

		await fetchTeams();
		fetchConnectedChannels();
	};

	/** get team id */
	getTeamId = (webUrl: string): string => {
		const webUrlObj = new URL(webUrl);
		return webUrlObj.searchParams.get("groupId");
	};

	/** get team that channel is a part of */
	getTeamName = (webUrl: string): string | undefined => {
		const { getTeamId } = this;
		const teamId = getTeamId(webUrl);
		return this.teamDictionary[teamId]?.displayName;
	};

	/** Remove connection to channel */
	handleRemoveConnectionToChannel = (): void => {
		rightPaneStore.openDialog(ConfirmDialog, {
			category: "warn",
			confirmRes: "Remove",
			messageRes: "RemoveConnectionToChannel_Confirm",
			messageResParams: [routerStore.locationType],
			titleRes: "RemoveConnectionToChannel_Prompt",
			onConfirm: async () => {
				this.resetConnectedChannels();
				this.resetTeamsAndChannels();
				this.navigationToConnectToChannel();
				await updateChannelOnNode(
					routerStore.locationUrl,
					this.connectedChannelId,
					UpdateChannelOnNodeOption.REMOVE,
					ATTR_ID_MSTEAMS_CHANNELS,
				),
				this.fetchConnectChannelsAndTeams();
			},
		});
	};

	/** open in App */
	openInMSTeamsApp = (): void => {
		const { selectedChannel } = this;
		const { webUrl } = selectedChannel;
		window.open(webUrl);
	};

	/** recover from error */
	@action recover = (): void => {
		this.navigateToPanel(MSTeamsPanel.CONNECT_TO_MSTEAMS);
		this._error = null;
	};

	/** sign in to ms teams */
	signIn = (): void => {
		const { msTeamsPermissionsUrl } = this;
		window.location.assign(msTeamsPermissionsUrl);
	};

	/** validate team */
	@action validateTeam = (): boolean => {
		this._teamIdErrorResource = this.newTeamId ? "" : "MSTeamsTeamNameNotEmpty";
		return !this._teamIdErrorResource;
	};

	//#endregion

	constructor() {
		super();
		makeObservable(this);
	}
}

export const MSTeamsStoreContext = createContext<MSTeamsStore>(undefined);
export const useMSTeamsStore = (): MSTeamsStore => useContext(MSTeamsStoreContext);
